import { useEffect, useRef, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, ClipboardText, Image as ImageIcon, Eye } from "@phosphor-icons/react";
import { CurrencyInput, DatePicker, MonthYearPicker } from "@/components/inputs";
import { DeleteConfirmButton } from "@/components/DeleteConfirmButton";
import {
  RFQStatusBadge, RFQInfoGrid, RFQItemsTable, RFQItemDetailModal, RFQQuotationBox, RFQTimeline,
} from "@/components/RFQDetail";
import {
  RFQ_UOM_OPTIONS, RFQ_DEPT_SUGGESTIONS, calcGrandTotal, fmtDateTime,
  RFQ_MONTHS,
} from "@/lib/rfq";
import { useEntryGuard } from "@/lib/useEntryGuard";

const emptyItem = () => ({
  item_name: "", specification: "", qty: "", uom: "", price: "", description: "", image_path: null,
});

export default function RFQPage() {
  const now = new Date();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [detail, setDetail] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const fileRefs = useRef({});
  const [form, setForm] = useState({ department: "", company_name: "", up_to: "", company_address: "", required_date: "", notes: "", items: [emptyItem()] });
  const [deptCustom, setDeptCustom] = useState("");
  const DEPT_OTHER = "__other__";

  const load = (selectedMonth = month, selectedYear = year) => {
    setLoading(true);
    api.get("/rfqs/me", { params: { month: selectedMonth, year: selectedYear } }).then((res) => {
      setList(res.data);
      setDetail((prev) => (prev ? res.data.find((x) => x.id === prev.id) || null : prev));
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(month, year); }, [month, year]);

  const setItem = (i, k, v) => {
    const arr = [...form.items];
    arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, items: arr });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, emptyItem()] });
  const removeItem = (i) => {
    if (form.items.length === 1) return;
    setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  };

  const uploadImage = async (i, file) => {
    if (!file) return;
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItem(i, "image_path", res.data.path);
      toast.success("Gambar terupload");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingIdx(null);
    }
  };

  const submit = async () => {
    const deptFinal = form.department === DEPT_OTHER ? deptCustom.trim() : form.department;
    if (!deptFinal) { toast.error("Department wajib dipilih"); return; }
    if (!form.company_name.trim()) { toast.error("Nama PT wajib diisi"); return; }
    if (!form.required_date) { toast.error("Tanggal dibutuhkan wajib diisi"); return; }
    for (let idx = 0; idx < form.items.length; idx++) {
      const it = form.items[idx];
      if (!it.item_name.trim()) { toast.error(`Nama item baris ${idx + 1} wajib diisi`); return; }
      if (!Number(it.qty) || Number(it.qty) <= 0) { toast.error(`Quantity baris ${idx + 1} harus lebih dari 0`); return; }
      if (!it.uom) { toast.error(`UOM baris ${idx + 1} wajib dipilih`); return; }
      if (Number(it.price) < 0 || it.price === "") { toast.error(`Harga baris ${idx + 1} tidak boleh negatif`); return; }
    }
    setSaving(true);
    try {
      await api.post("/rfqs", {
        department: deptFinal,
        company_name: form.company_name.trim(),
        up_to: form.up_to.trim(),
        company_address: form.company_address.trim(),
        required_date: form.required_date,
        notes: form.notes.trim(),
        items: form.items.map((it) => ({
          item_name: it.item_name.trim(),
          specification: (it.specification || "").trim(),
          qty: Number(it.qty),
          uom: it.uom,
          price: Number(it.price || 0),
          description: (it.description || "").trim(),
          image_path: it.image_path,
        })),
      });
      toast.success("RFQ berhasil disubmit");
      setForm({ department: "", company_name: "", up_to: "", company_address: "", required_date: "", notes: "", items: [emptyItem()] });
      setDeptCustom("");
      setOpen(false);
      if (month !== currentMonth || year !== currentYear) {
        setMonth(currentMonth);
        setYear(currentYear);
      } else {
        load(currentMonth, currentYear);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadQuotation = async (rfq) => {
    setDownloading(true);
    try {
      const res = await api.get(`/rfqs/${rfq.id}/quotation`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = rfq.quotation?.file_name || "quotation";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      toast.error("Gagal mengunduh quotation");
    } finally {
      setDownloading(false);
    }
  };

  const grandPreview = calcGrandTotal(form.items);

  // Guard: tap nyasar (mis. setelah menutup picker native di HP) tidak boleh
  // menutup form berisi isian. Minta konfirmasi dulu bila form kotor.
  const [confirmClose, setConfirmClose] = useState(false);
  const entryGuarded = useEntryGuard(open);
  const isDirty =
    form.department !== "" || deptCustom !== "" || form.company_name !== "" ||
    form.up_to !== "" || form.company_address !== "" || form.required_date !== "" ||
    form.notes !== "" || form.items.some((it) =>
      it.item_name !== "" || it.specification !== "" || it.qty !== "" ||
      it.uom !== "" || it.price !== "" || it.description !== "" || it.image_path);
  const requestCloseForm = (next) => {
    if (!next && isDirty) {
      setConfirmClose(true);
      return;
    }
    setOpen(next);
  };

  const monthDone = list.filter((r) => r.status === "completed").length;
  const periodLabel = `${RFQ_MONTHS[month - 1]} ${year}`;
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  return (
    <AppLayout title="Request For Quotation">
      <div className="max-w-5xl space-y-6 animate-fade-up">
        <Card className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold tracking-[0.15em] uppercase text-muted-foreground">Periode RFQ</p>
            <p className="font-heading text-lg sm:text-xl font-bold tracking-tight" data-testid="rfq-month-label">{periodLabel}</p>
            <p className="text-xs text-muted-foreground mt-0.5" data-testid="rfq-month-summary">{list.length} RFQ · {monthDone} selesai</p>
          </div>
          <MonthYearPicker
            month={month}
            year={year}
            onChange={(nextMonth, nextYear) => { setMonth(nextMonth); setYear(nextYear); }}
            years={Array.from(new Set([currentYear - 2, currentYear - 1, currentYear, currentYear + 1])).filter((optionYear) => optionYear >= 2023 && optionYear <= 2030)}
            monthTestId="rfq-month-filter"
            yearTestId="rfq-year-filter"
            className="w-full sm:w-64"
          />
        </Card>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">Status otomatis <Badge variant="outline" className="ml-1 border-[hsl(var(--warning))] text-[hsl(var(--warning))]">Submitted</Badge></p>
          <Dialog open={open} onOpenChange={requestCloseForm}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-rfq-button">
                <Plus size={18} weight="bold" /> Buat RFQ
              </Button>
            </DialogTrigger>
            <DialogContent className={`max-w-3xl max-h-[90vh] overflow-y-auto${entryGuarded ? " pointer-events-none" : ""}`}>
              <DialogHeader><DialogTitle>Buat RFQ Baru</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Department</Label>
                    <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                      <SelectTrigger data-testid="rfq-department-input"><SelectValue placeholder="Pilih department" /></SelectTrigger>
                      <SelectContent className="duration-200">
                        {RFQ_DEPT_SUGGESTIONS.map((d, i) => (
                          <SelectItem key={d} value={d} className="anim-select-item" style={{ animationDelay: `${i * 35}ms` }}>{d}</SelectItem>
                        ))}
                        <SelectItem value={DEPT_OTHER} className="anim-select-item" style={{ animationDelay: `${RFQ_DEPT_SUGGESTIONS.length * 35}ms` }}>Lainnya (isi manual)…</SelectItem>
                      </SelectContent>
                    </Select>
                    {form.department === DEPT_OTHER && (
                      <Input
                        value={deptCustom}
                        onChange={(e) => setDeptCustom(e.target.value)}
                        placeholder="Tulis nama department"
                        data-testid="rfq-department-custom"
                        className="mt-2"
                      />
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tanggal Dibutuhkan</Label>
                    <DatePicker value={form.required_date} onChange={(v) => setForm({ ...form, required_date: v })} testId="rfq-required-date" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Nama PT</Label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    placeholder="Contoh: PT Maju Jaya Abadi"
                    data-testid="rfq-company-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>U.P (Untuk Perhatian)</Label>
                  <Input
                    value={form.up_to}
                    onChange={(e) => setForm({ ...form, up_to: e.target.value })}
                    placeholder="Contoh: Bpk. Andi — Bagian Penjualan"
                    data-testid="rfq-upto-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Alamat PT</Label>
                  <Textarea
                    value={form.company_address}
                    onChange={(e) => setForm({ ...form, company_address: e.target.value })}
                    placeholder="Alamat lengkap perusahaan…"
                    data-testid="rfq-address-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Catatan Umum</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Catatan tambahan…" data-testid="rfq-notes-input" />
                </div>

                <div className="flex items-center justify-between">
                  <h4 className="font-heading font-bold">Item ({form.items.length})</h4>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={addItem} data-testid="add-rfq-item-button">
                    <Plus size={16} /> Add Item
                  </Button>
                </div>

                {form.items.map((it, i) => (
                  <Card key={i} className="p-4 space-y-3" data-testid={`rfq-item-row-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold">#{i + 1}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold tabular-nums">{formatRupiah(Number(it.qty || 0) * Number(it.price || 0))}</span>
                        {form.items.length > 1 && (
                          <DeleteConfirmButton
                            title="Hapus item RFQ?"
                            description="Item ini akan dihapus dari formulir RFQ."
                            onConfirm={() => removeItem(i)}
                            triggerTestId={`remove-rfq-item-${i}`}
                            confirmTestId={`confirm-remove-rfq-item-${i}`}
                          />
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label>Item</Label>
                        <Input value={it.item_name} onChange={(e) => setItem(i, "item_name", e.target.value)} placeholder="Nama barang" data-testid={`rfq-item-name-${i}`} />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label>Spesifikasi</Label>
                        <Textarea value={it.specification} onChange={(e) => setItem(i, "specification", e.target.value)} placeholder="Spesifikasi teknis barang…" data-testid={`rfq-item-spec-${i}`} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Quantity</Label>
                        <Input type="number" min="0" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} placeholder="0" data-testid={`rfq-item-qty-${i}`} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>UOM</Label>
                        <Select value={it.uom} onValueChange={(v) => setItem(i, "uom", v)}>
                          <SelectTrigger data-testid={`rfq-item-uom-${i}`}><SelectValue placeholder="Pilih satuan" /></SelectTrigger>
                          <SelectContent>
                            {RFQ_UOM_OPTIONS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Harga per Unit (Rp)</Label>
                        <CurrencyInput value={it.price} onChange={(num) => setItem(i, "price", num)} data-testid={`rfq-item-price-${i}`} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Gambar</Label>
                        <div className="flex items-center gap-2">
                          <input
                            ref={(el) => { fileRefs.current[i] = el; }}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            data-testid={`rfq-item-image-${i}`}
                            onChange={(e) => { uploadImage(i, e.target.files?.[0]); e.target.value = ""; }}
                          />
                          <Button variant="outline" size="sm" className="gap-1.5" disabled={uploadingIdx === i} onClick={() => fileRefs.current[i]?.click()} data-testid={`rfq-item-image-button-${i}`}>
                            <ImageIcon size={16} /> {uploadingIdx === i ? "Mengupload…" : it.image_path ? "Ganti" : "Upload"}
                          </Button>
                          {it.image_path && <span className="text-xs text-[hsl(var(--success))]">Terupload ✓</span>}
                        </div>
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label>Keterangan Tambahan</Label>
                        <Input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} placeholder="Catatan item…" data-testid={`rfq-item-desc-${i}`} />
                      </div>
                    </div>
                  </Card>
                ))}

                <div className="flex items-center justify-between pt-2 border-t-2 border-foreground">
                  <span className="font-heading font-bold">Grand Total</span>
                  <span className="font-heading text-xl font-bold tabular-nums text-primary" data-testid="rfq-grand-total">{formatRupiah(grandPreview)}</span>
                </div>

                <Button onClick={submit} disabled={saving} className="w-full gap-2" data-testid="submit-rfq-button">
                  <ClipboardText size={18} /> {saving ? "Mengirim…" : "Submit RFQ"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
            <AlertDialogContent data-testid="rfq-discard-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle>Tutup form?</AlertDialogTitle>
                <AlertDialogDescription>
                  Isian RFQ yang belum disubmit akan hilang. Lanjut mengisi atau tutup form?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="rfq-discard-cancel">Lanjut Mengisi</AlertDialogCancel>
                <AlertDialogAction
                  variant="destructive"
                  onClick={() => { setConfirmClose(false); setOpen(false); }}
                  data-testid="rfq-discard-confirm-button"
                >
                  Ya, Tutup
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <Card className="overflow-hidden overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>RFQ Number</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead className="text-center">Item</TableHead>
                <TableHead className="text-right">Grand Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Update</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Memuat…</TableCell></TableRow>
              ) : list.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Tidak ada RFQ pada {periodLabel}.</TableCell></TableRow>
              ) : list.map((r, idx) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => setDetail(r)} data-testid={`rfq-row-${idx}`}>
                  <TableCell className="font-medium">{r.rfq_number}</TableCell>
                  <TableCell>{r.request_date}</TableCell>
                  <TableCell className="text-center">{r.item_count}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatRupiah(r.grand_total)}</TableCell>
                  <TableCell><RFQStatusBadge status={r.status} /></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtDateTime(r.updated_at)}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="gap-1.5" onClick={(e) => { e.stopPropagation(); setDetail(r); }} data-testid={`view-rfq-${idx}`}>
                      <Eye size={16} /> Detail
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="rfq-detail-modal">
            <DialogHeader><DialogTitle>Detail {detail?.rfq_number}</DialogTitle></DialogHeader>
            {detail && (
              <div className="space-y-5">
                <RFQInfoGrid rfq={detail} />
                <div>
                  <h4 className="font-heading font-bold mb-2">Item Details</h4>
                  <p className="text-xs text-muted-foreground mb-2">Klik baris untuk melihat detail item.</p>
                  <Card className="overflow-hidden overflow-x-auto">
                    <RFQItemsTable
                      items={detail.items}
                      onSelect={(item, index) => setSelectedItem({ item, index })}
                    />
                  </Card>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm font-bold">Grand Total</span>
                    <span className="font-heading font-bold tabular-nums text-primary">{formatRupiah(detail.grand_total)}</span>
                  </div>
                </div>
                <RFQQuotationBox rfq={detail} onDownload={() => downloadQuotation(detail)} downloading={downloading} />
                <RFQTimeline history={detail.history} />
              </div>
            )}
          </DialogContent>
        </Dialog>

        <RFQItemDetailModal
          item={selectedItem?.item}
          index={selectedItem?.index}
          onClose={() => setSelectedItem(null)}
        />
      </div>
    </AppLayout>
  );
}
